import {useCallback, useEffect} from 'react';
import {useBlocker} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {bounceMessageModal} from '../layout/userMessages/UserMessage';

interface UseUnsavedChangesWarningProps {
    hasUnsavedChanges: boolean;
}

/**
 * Hook to warn users when they try to leave the page with unsaved changes
 * @param hasUnsavedChanges - Whether there are unsaved changes
 */
export function useUnsavedChangesWarning({hasUnsavedChanges}: UseUnsavedChangesWarningProps) {
    const {t} = useTranslation('whatsapp');

    // Block navigation when there are unsaved changes
    const blocker = useBlocker(
        ({currentLocation, nextLocation}) => hasUnsavedChanges && currentLocation.pathname !== nextLocation.pathname
    );

    // Memoize blocker action handlers
    const handleExitAnyway = useCallback(() => {
        blocker.proceed?.();
    }, [blocker]);

    const handleCancel = useCallback(() => {
        blocker.reset?.();
    }, [blocker]);

    // Handle blocked navigation
    useEffect(() => {
        if(blocker.state !== 'blocked') {
            return;
        }

        if(!hasUnsavedChanges) {
            blocker.reset?.();
            return;
        }

        bounceMessageModal(
            t('unsavedChangesWarning'),
            'attention',
            {
                text: t('exitAnyway'),
                onClick: handleExitAnyway
            },
            {
                text: t('cancel'),
                onClick: handleCancel
            }
        );
    }, [blocker.state, hasUnsavedChanges, t, handleExitAnyway, handleCancel]);

    // Memoize the beforeunload handler to avoid recreating on every render
    const handleBeforeUnload = useCallback((e: BeforeUnloadEvent) => {
        e.preventDefault();
        return '';
    }, []);

    // Also warn on browser close/refresh
    useEffect(() => {
        if(!hasUnsavedChanges) {
            return;
        }

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [hasUnsavedChanges, handleBeforeUnload]);
}
